// Pitching Command Center — playerspitch teams only.
// FPS→outcome funnel, walks-that-scored callout, strike% by pitcher,
// Game Planning (Command 0–99 + innings forecast), and the ported
// "Available to Pitch" rest-eligibility list (safety-relevant — the
// eligibility logic is verbatim from the old pitching tab).

import React from 'react';
import { GS } from '../../lib/designTokens';
import { fmtStat } from '../../lib/stat-format';
import { Card, ScoreBadge, shortName } from './ui';
import { DSSMeter } from '../DSSMeter';
import { commandScore, forecastInnings, PitchCapRules } from '../../lib/pitching-plan';
import type { SeasonData } from './useSeasonStats';

const Legend = () => (
  <div className="flex gap-3 mt-2 flex-wrap">
    {[['Out', '#0EA372'], ['Hit', '#F59E0B'], ['Walk', '#E5484D']].map(([l, c]) => (
      <span key={l} className="flex items-center gap-1.5 text-[9px] font-extrabold uppercase" style={{ color: GS.ink2, letterSpacing: '0.05em' }}>
        <span className="w-2.5 h-2.5 rounded" style={{ background: c }} />{l}
      </span>
    ))}
  </div>
);

export const PitchingCommandCenter = ({ data, capRules, isHeadCoach }: {
  data: SeasonData;
  capRules: PitchCapRules | null;
  isHeadCoach?: boolean;
}) => {
  const pitchers = Object.values(data.stats)
    .filter((s: any) => s.pitching.ip > 0 || s.pitching.pitches > 0 || (s.player.pitchingLog || []).length > 0)
    .sort((a: any, b: any) => (b.pitching.pitches || 0) - (a.pitching.pitches || 0));

  // Team FPS outcome splits, BF-weighted where reported
  let fpsSum = 0, fpsW = 0;
  const splits = { fpsoPct: [0, 0], fpshPct: [0, 0], fpswPct: [0, 0] } as Record<string, [number, number]>;
  Object.values(data.stats).forEach((s: any) => {
    const p = s.pitching;
    const w = p.bf || 0;
    if (w <= 0) return;
    if (p.fpsPct != null) { fpsSum += p.fpsPct * w; fpsW += w; }
    (['fpsoPct', 'fpshPct', 'fpswPct'] as const).forEach(k => {
      if (p[k] != null) { splits[k][0] += p[k] * w; splits[k][1] += w; }
    });
  });
  const teamFps = fpsW > 0 ? fpsSum / fpsW : null;
  const outcome = Object.fromEntries(
    Object.entries(splits).map(([k, [sum, w]]) => [k, w > 0 ? sum / w : null])
  ) as Record<'fpsoPct' | 'fpshPct' | 'fpswPct', number | null>;
  const hasOutcome = outcome.fpsoPct != null || outcome.fpshPct != null || outcome.fpswPct != null;
  const outcomeTotal = (outcome.fpsoPct ?? 0) + (outcome.fpshPct ?? 0) + (outcome.fpswPct ?? 0);

  const { teamERA, teamFIP, teamBABIP, teamDSSResult, tIP } = data.teamPitching;

  const planning = pitchers
    .map((s: any) => {
      const cmd = commandScore(s.pitching);
      const forecast = forecastInnings(s.pitching.pIp, capRules);
      if (!cmd && !forecast) return null;
      return { player: s.player, pitching: s.pitching, cmd, forecast };
    })
    .filter((r): r is NonNullable<typeof r> => r != null);

  if (pitchers.length === 0) {
    return (
      <Card className="p-5 text-center">
        <p className="text-sm font-black" style={{ color: GS.ink }}>No pitching data yet</p>
        <p className="text-[11px] font-semibold mt-1" style={{ color: GS.ink2 }}>Complete a game or import a GameChanger CSV.</p>
      </Card>
    );
  }

  return (
    <div className="space-y-3">
      {/* FPS funnel */}
      {(teamFps != null || hasOutcome) && (
        <Card className="p-4">
          {teamFps != null && (
            <>
              <div className="flex justify-between text-[10px] font-extrabold mb-1.5" style={{ color: GS.ink }}>
                <span>First-pitch strikes</span>
                <span>{fmtStat(teamFps, 'pct')} of batters</span>
              </div>
              <div className="h-5 rounded-lg overflow-hidden flex" style={{ background: GS.panel2 }} role="img"
                aria-label={`First pitch strike rate ${Math.round(teamFps * 100)} percent`}>
                <div className="h-full flex items-center justify-center text-[10px] font-black text-ink" style={{ width: `${Math.round(teamFps * 100)}%`, background: '#0EA372', minWidth: 34 }}>
                  {fmtStat(teamFps, 'pct')}
                </div>
                <div className="h-full flex items-center justify-center text-[10px] font-black" style={{ flex: 1, color: GS.ink2 }}>
                  {fmtStat(1 - teamFps, 'pct')} ball
                </div>
              </div>
            </>
          )}
          {hasOutcome && outcomeTotal > 0 && (
            <>
              <div className="flex justify-between text-[10px] font-extrabold mt-3.5 mb-1.5" style={{ color: GS.ink }}>
                <span>…and when you get ahead 0-1</span>
              </div>
              <div className="h-5 rounded-lg overflow-hidden flex gap-px" role="img"
                aria-label={`After a first pitch strike: ${fmtStat(outcome.fpsoPct, 'pct')} outs, ${fmtStat(outcome.fpshPct, 'pct')} hits, ${fmtStat(outcome.fpswPct, 'pct')} walks`}>
                {([['fpsoPct', '#0EA372', 'out'], ['fpshPct', '#F59E0B', ''], ['fpswPct', '#E5484D', '']] as const).map(([k, c, suffix]) => {
                  const v = outcome[k];
                  if (v == null || v <= 0) return null;
                  return (
                    <div key={k} className="h-full flex items-center justify-center text-[10px] font-black text-ink" style={{ width: `${(v / outcomeTotal) * 100}%`, background: c, minWidth: 30 }}>
                      {fmtStat(v, 'pct')}{suffix ? ` ${suffix}` : ''}
                    </div>
                  );
                })}
              </div>
              <Legend />
            </>
          )}
          {outcome.fpsoPct != null && (
            <p className="text-[10px] font-semibold leading-relaxed mt-3 pt-3" style={{ color: GS.ink2, borderTop: `1px solid ${GS.line}` }}>
              Get strike one and hitters are out <b style={{ color: GS.ink }}>{fmtStat(outcome.fpsoPct, 'pct')} of the time</b>.
              {outcome.fpswPct != null && outcome.fpswPct >= 0.2 && <> The {fmtStat(outcome.fpswPct, 'pct')} that still walk is the practice-plan headline.</>}
            </p>
          )}
        </Card>
      )}

      {/* Walks that scored */}
      {data.selfInflicted && (
        <Card className="p-4 flex items-center gap-3 border" style={{ background: 'rgba(229,72,77,0.06)', borderColor: 'rgba(229,72,77,0.25)' }}>
          <div className="text-[22px] font-black shrink-0" style={{ color: GS.red }}>
            {data.selfInflicted.walksScored}<span className="text-xs" style={{ color: GS.ink2 }}>/{data.selfInflicted.runsAllowed}</span>
          </div>
          <p className="text-[11px] font-semibold leading-snug" style={{ color: GS.ink2 }}>
            <b style={{ color: GS.ink }}>Walks that scored.</b>{' '}
            {fmtStat(data.selfInflicted.share, 'pct')} of all runs against you started as a free pass. Walks are runs at this level.
          </p>
        </Card>
      )}

      {/* Strike % by pitcher */}
      <Card className="px-4 py-3">
        <div className="flex justify-between text-[10px] font-extrabold mb-1" style={{ color: GS.ink }}>
          <span>Strike % by pitcher</span>
          <span style={{ color: GS.ink3 }}>BB/inning</span>
        </div>
        {pitchers.filter((s: any) => s.pitching.sPct != null).map((s: any) => (
          <div key={s.player.id} className="flex items-center gap-2.5 py-1.5">
            <span className="w-24 text-[11px] font-extrabold truncate shrink-0" style={{ color: GS.ink }}>
              {shortName(s.player.name)} #{s.player.number}
            </span>
            <div className="flex-1 h-2.5 rounded overflow-hidden" style={{ background: GS.panel2 }}>
              <div className="h-full rounded" style={{ width: `${Math.round((s.pitching.sPct || 0) * 100)}%`, background: GS.green2 }} />
            </div>
            <span className="w-20 text-right text-[11px] font-black shrink-0" style={{ color: GS.ink }}>
              {fmtStat(s.pitching.sPct, 'pct')} <span className="text-[9px] font-extrabold" style={{ color: GS.ink3 }}>· {fmtStat(s.pitching.bbInn, 'ratio1')}</span>
            </span>
          </div>
        ))}
      </Card>

      {/* Game planning: Command + innings forecast */}
      {planning.length > 0 && (
        <Card>
          <div className="flex justify-between px-4 pt-3.5 pb-1 text-[10px] font-extrabold" style={{ color: GS.ink }}>
            <span>Game planning</span>
            <span style={{ color: GS.ink3 }}>Command 0–99</span>
          </div>
          {planning.map(({ player, pitching, cmd, forecast }, i) => (
            <div key={player.id} className="flex items-center gap-3 px-4 py-2.5" style={{ borderTop: i > 0 ? `1px solid ${GS.line}` : undefined }}>
              {cmd
                ? <ScoreBadge score={cmd.score} label="CMD" />
                : <div className="w-10 h-10 rounded-xl flex items-center justify-center text-[10px] font-black shrink-0" style={{ background: GS.panel2, color: GS.ink3 }}>—</div>}
              <div className="flex-1 min-w-0">
                <p className="text-xs font-extrabold truncate" style={{ color: GS.ink }}>{player.name}</p>
                <p className="text-[10px] font-semibold mt-0.5" style={{ color: GS.ink2 }}>
                  {pitching.pIp != null ? `${fmtStat(pitching.pIp, 'ratio1')} pitches per inning` : `${fmtStat(pitching.ipTrue ?? pitching.ip, 'ip')} IP`}
                </p>
              </div>
              {forecast?.forecastIP != null && (
                <div className="text-right shrink-0">
                  <div className="text-sm font-black" style={{ color: GS.greenInk }}>~{fmtStat(forecast.forecastIP, 'ip')} IP</div>
                  <div className="text-[8px] font-extrabold uppercase" style={{ color: GS.ink3, letterSpacing: '0.05em' }}>
                    {forecast.capPitches ? `on a ${forecast.capPitches}-pitch cap` : 'innings cap'}
                  </div>
                </div>
              )}
            </div>
          ))}
          <p className="px-4 py-2.5 text-[10px] font-semibold leading-relaxed" style={{ color: GS.ink3, borderTop: `1px solid ${GS.line}` }}>
            Command is the <b style={{ color: GS.ink }}>stats-side read of your Control grade</b> (1–5 in Roster): what the games say, next to what you scouted.
            {isHeadCoach ? ' Big gaps show in each pitcher’s detail sheet.' : ''} Caps follow your league's pitch-count rules.
          </p>
        </Card>
      )}

      {/* Team DSS — ported verbatim */}
      {teamDSSResult != null && teamERA != null && teamFIP != null && (
        <Card>
          <div className="px-4 pt-3 pb-1">
            <p className="text-[11px] font-black uppercase" style={{ color: GS.ink, letterSpacing: '0.14em' }}>Team Defensive Score</p>
          </div>
          <DSSMeter
            era={teamERA}
            fip={teamFIP}
            babip={teamBABIP}
            dss={teamDSSResult.dss}
            label={teamDSSResult.label}
            confidence={teamDSSResult.confidence}
            insight={(() => {
              const gap = teamERA - teamFIP;
              const babipDisplay = teamBABIP != null ? `.${Math.round(teamBABIP * 1000).toString().padStart(3, '0')}` : null;
              if (teamDSSResult.confidence === 'low') return `Only ${tIP.toFixed(1)} team innings logged. Check back after more games for a reliable read.`;
              if (gap > 2.0 && teamBABIP != null && teamBABIP > 0.340) return `Your staff is pitching better than the ERA suggests. Team FIP of ${teamFIP.toFixed(2)} reflects strong underlying performance, but a ${babipDisplay} BABIP indicates balls in play are finding gaps at a high rate, a team defense issue, not a pitching issue.`;
              if (gap > 1.0) return `Team ERA (${teamERA.toFixed(2)}) is running above FIP (${teamFIP.toFixed(2)}). Your pitchers are competing well; some defensive or sequencing factors may be pushing the ERA up.`;
              if (gap < -1.5) return `Team ERA looks favorable, but FIP (${teamFIP.toFixed(2)}) suggests some defensive or sequencing help. Strong results. Keep an eye on whether they hold.`;
              return `Team ERA (${teamERA.toFixed(2)}) and FIP (${teamFIP.toFixed(2)}) are well-matched. Results are reflecting the staff's actual performance.`;
            })()}
          />
        </Card>
      )}
    </div>
  );
};

/** Available-to-Pitch — FREE tier (safety feature), rendered outside the Elite lock. */
export const AvailableToPitchCard = ({ data }: { data: SeasonData }) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const rows = Object.values(data.stats)
    .filter((s: any) => s.pitching.ip > 0 || s.pitching.pitches > 0 || (s.player.pitchingLog || []).length > 0)
    .map((s: any) => {
      const player = s.player;
      const log: any[] = player.pitchingLog || [];
      const sortedLog = [...log].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
      const lastEntry = sortedLog[0];
      const gcOuting = data.lastGCOuting[player.id];
      const useGC = gcOuting && (!lastEntry || gcOuting.date > (lastEntry.date || ''));
      const lastLabel = useGC
        ? `${gcOuting.pitches}p vs ${gcOuting.opponent}`
        : lastEntry
        ? `${lastEntry.pitchCount}p vs ${lastEntry.opponent || '?'}`
        : '—';
      const eligibleDate = lastEntry?.eligibleDate ? new Date(lastEntry.eligibleDate) : null;
      if (eligibleDate) eligibleDate.setHours(0, 0, 0, 0);
      const isEligible = !eligibleDate || eligibleDate <= today;
      const nextAvailable = !eligibleDate
        ? '—'
        : isEligible
        ? 'Today'
        : eligibleDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      return { player, lastLabel, isEligible, nextAvailable };
    })
    .sort((a, b) => Number(b.isEligible) - Number(a.isEligible));

  if (rows.length === 0) return null;

  return (
    <Card>
      <div className="flex justify-between px-4 pt-3.5 pb-1 text-[10px] font-extrabold" style={{ color: GS.ink }}>
        <span>Available to pitch</span>
        <span style={{ color: GS.ink3 }}>last outing</span>
      </div>
      {rows.map(({ player, lastLabel, isEligible, nextAvailable }, i) => (
        <div key={player.id} className="flex items-center gap-3 px-4 py-2.5" style={{ borderTop: i > 0 ? `1px solid ${GS.line}` : undefined }}>
          <div className="flex-1 min-w-0">
            <p className="text-xs font-extrabold truncate" style={{ color: GS.ink }}>{player.name}</p>
            <p className="text-[10px] font-semibold mt-0.5 truncate" style={{ color: GS.ink2 }}>{lastLabel}</p>
          </div>
          <span
            className="shrink-0 text-[9px] font-black uppercase rounded-lg px-2.5 py-1"
            style={isEligible
              ? { background: GS.greenSoft, color: GS.greenInk, border: `1px solid ${GS.green}`, letterSpacing: '0.07em' }
              : { background: GS.goldSoft, color: GS.goldInk, border: `1px solid ${GS.gold}`, letterSpacing: '0.07em' }}
          >
            {isEligible ? '● Ready' : nextAvailable}
          </span>
        </div>
      ))}
    </Card>
  );
};
